import React, {FC} from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  TextField,
  DialogActions,
  Button
} from '@mui/material';
import {v4 as uuidv4} from 'uuid';
import {useForm} from 'react-hook-form';
import {yupResolver} from '@hookform/resolvers/yup';
import * as Yup from 'yup';
import {getCmsData} from '@shared/services/service-handlers/PrivateService';
import EncryptUtils from 'src/utils/EncryptUtils';
import {LoadingButton} from '@mui/lab';
import {getErrorMessage} from '@shared/utils/ErrorUtils';
import SnackBarConfig from '@components/snackbar/SnackbarConfig';
import {enqueueSnackbar} from 'notistack';

interface AddLinkCodeDialogProps {
  token: string | undefined;
  parentId: string | undefined;
  setTreeData?: any;
  data?: any;
  // eslint-disable-next-line react/boolean-prop-naming
  open: boolean;
  onClose: () => void;
  onCancel: () => void;
}

interface FormInputs {
  nametyped: string;
  linkCode: string;
}

const AddLinkCodeDialog: FC<AddLinkCodeDialogProps> = ({
  token,
  parentId,
  open,
  setTreeData,
  onClose,
  onCancel
}) => {
  // Define the Yup schema
  const schema = Yup.object({
    nametyped: Yup.string()
      .required('Name is required')
      .min(3, 'Name must be at least 3 characters long'),
    linkCode: Yup.string()
      .required('Link Code is required')
      .url('Link Code must be a valid URL')
  });
  window.addEventListener('keydown', event => {
    // Ignore key events if the target is an input, textarea, or contenteditable element
    const target = event.target as HTMLElement;

    // Check if the target is an input, textarea, or contenteditable element
    const isInputField =
      target.tagName === 'INPUT' ||
      target.tagName === 'TEXTAREA' ||
      target.isContentEditable;

    if (isInputField) {
      return; // Exit if the focus is on an input field
    }

    // Handle other global key events here
    if (event.key === 'a' || event.key === 'd' || event.key === 'r') {
      console.log(`Key pressed: ${event.key}`);
      // Add your custom logic for these keys
    }
  });
  // Initialize React Hook Form
  const {
    register,
    handleSubmit,
    formState: {errors, isSubmitting}
  } = useForm<FormInputs>({
    resolver: yupResolver(schema)
  });

  // Form submit handler
  const onSubmit = async (data: FormInputs) => {
    try {
      const nodeData = {
        Name: data.nametyped,
        ParentId: parentId,
        NodeType: 'Link code',
        Icon: 'link_icon',
        Access: true,
        MimeType: 'linkcode',
        UploadedFile: data.linkCode,
        children: []
      };

      const requestData: any = {
        sql_data: token,
        data: nodeData,
        operation: 'INSERT'
      };
      const encryptRequestData = EncryptUtils.encryptedData(requestData);
      const response = await getCmsData('LMS', 'content-management', {
        tk: encryptRequestData
      });

      const {Name, Id} = response?.data[0] || {};
      // Update tree data
      if (Name && Id) {
        setTreeData((prevTreeData: any[]) => {
          const updateTree = (nodes: any[]): any[] =>
            nodes.map(node => {
              if (node.Id === parentId) {
                return {
                  ...node,
                  hasDocuments: true, // Set hasDocuments to true
                  children: [
                    ...(node.children || []),
                    {
                      Id: Id,
                      Name: Name,
                      ParentId: parentId,
                      NodeType: 'Link code',
                      Icon: 'link_icon',
                      Access: true,
                      MimeType: 'linkcode',
                      UploadedFile: data.linkCode,
                      children: []
                    }
                  ]
                };
              }
              if (node.children) {
                return {
                  ...node,
                  children: updateTree(node.children)
                };
              }
              return node;
            });

          return updateTree(prevTreeData);
        });
      }

      onClose();
      enqueueSnackbar('The Link is Added', SnackBarConfig.getSuccess());
    } catch (e) {
      console.error('Error saving folder:', e);
      onClose();
      const errMessage = getErrorMessage(e);
      enqueueSnackbar(errMessage, SnackBarConfig.getError());
    }
  };

  return (
    <Dialog open={open} onClose={onClose} aria-labelledby="form-dialog-title">
      <DialogTitle id="form-dialog-title">Add Link</DialogTitle>
      <DialogContent>
        <form onSubmit={handleSubmit(onSubmit)}>
          <TextField
            {...register('nametyped')}
            label="Link Name"
            autoFocus
            fullWidth
            margin="dense"
            onKeyDown={event => {
              event.stopPropagation(); // Prevent the event from bubbling to global handlers
            }}
            variant="outlined"
            error={!!errors.nametyped}
            helperText={errors.nametyped?.message}
          />
          <TextField
            {...register('linkCode')}
            label="Link Code (URL)"
            fullWidth
            margin="dense"
            variant="outlined"
            onKeyDown={event => {
              event.stopPropagation(); // Prevent the event from bubbling to global handlers
            }}
            error={!!errors.linkCode}
            helperText={errors.linkCode?.message}
          />
          <DialogActions>
            <Button variant="outlined" color="inherit" onClick={onCancel}>
              Cancel
            </Button>
            <LoadingButton
              type="submit"
              variant="contained"
              color="success"
              onClick={handleSubmit(onSubmit)} // Correctly invoke handleSubmit
              loading={isSubmitting}>
              Save
            </LoadingButton>
          </DialogActions>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default AddLinkCodeDialog;
